import React from 'react';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from 'styled-components';

interface CustomDateTimeProps {
  value: string;
  onChange: (value: string) => void;
  minDateTime?: string;
  disablePast?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
}

const DatePickerContainer = styled.div`
  width: 100%;
  position: relative;
`;

// Create a properly typed styled DatePicker component
const StyledDatePicker = styled(DatePicker).withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<DatePickerProps & { $error?: boolean }>`
  width: 100% !important;
  padding: 12px 16px;
  border: 1px solid ${props => props.$error ? '#ef4444' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  color: #1e293b;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => props.$error ? '#ef4444' : '#e39435'};
    box-shadow: 0 0 0 3px ${props => props.$error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(227, 148, 53, 0.1)'};
  }

  &::placeholder {
    color: #94a3b8;
    font-size: 14px;
  }

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 16px;
  }
` as React.ComponentType<DatePickerProps & { $error?: boolean }>;

const HelperText = styled.div<{ $error?: boolean }>`
  margin-top: 4px;
  font-size: 12px;
  color: ${props => props.$error ? '#ef4444' : '#64748b'};
  min-height: 16px;

  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

export default function CustomDateTimePicker({
  value,
  onChange,
  minDateTime,
  disablePast = false,
  error = false,
  helperText,
  placeholder = "Select date and time",
}: CustomDateTimeProps) {
  
  // Convert string value to Date object
  const dateValue = value ? new Date(value) : null;
  
  
  // Calculate minimum date based on disablePast prop
  const getMinDate = () => {
    // Always return undefined to allow all date selections
    // Let validation handle past date restrictions instead
    return undefined;
  };

  const handleChange = (date: Date | Date[] | null) => {
    // Handle null case first
    if (!date) {
      onChange("");
      return;
    }

    // Handle single date selection (not range)
    const singleDate: Date = Array.isArray(date) ? date[0] : date;
    
    var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    let selectedDate = new Date(singleDate.valueOf() - tzoffset);
    let modifiedDate: any = selectedDate.toISOString().slice(0, -1);

    if (modifiedDate) {
      onChange(modifiedDate);
    } else {
      onChange("");
    }
  };

  // Filter times to disable past times for today
  const filterTime = (time: Date) => {
    if (!disablePast) return true;
    
    const now = new Date();
    const selectedDate = time;
    
    // If selected date is not today, allow all times
    if (selectedDate.toDateString() !== now.toDateString()) {
      return true;
    }
    
    // If it's today, only allow times after current time + 1 minute
    const minAllowedTime = new Date(now.getTime() + 60000); // +1 minute
    return selectedDate >= minAllowedTime;
  };

  return (
    <DatePickerContainer>
      <StyledDatePicker
        selected={dateValue}
        onChange={handleChange}
        showTimeSelect
        timeFormat="HH:mm"
        timeIntervals={1}
        dateFormat="dd/MM/yyyy HH:mm"
        placeholderText={placeholder}
        minDate={getMinDate()}
        filterTime={filterTime}
        $error={error}
        autoComplete="off"
        popperClassName="custom-datepicker-popper"
        calendarClassName="custom-datepicker-calendar"
        wrapperClassName="custom-datepicker-wrapper"
        className="custom-datepicker-input"
      />
      {helperText && (
        <HelperText $error={error}>
          {helperText}
        </HelperText>
      )}
    </DatePickerContainer>
  );
}
